import React, { useContext, useEffect, useCallback } from 'react';
import {
	IEditDishRestaurantPropertiesContext,
	EditDishRestaurantPropertiesContext,
} from '../../../../editDishRestaurantPropertiesState/editDishRestaurantPropertiesContext';
import { Form } from 'antd';
import { preventDefaultForEnterKey } from 'utils/helpers';
import Text from 'components/Text/Text';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import { RestaurantSelect } from './components/RestaurantSelect';
import { CategoryList } from 'components/ViewPage/CategoryList';
import { NoItemsNotification } from 'components/ViewPage/NoItemsNotification';
import { PriceInput } from './components/PriceInput';
import { OpenPriceInput } from './components/OpenPriceInput';
import { OpenPriceMaxThresholdInput } from './components/OpenPriceMaxThresholdInput';
import { HasOpenPriceCheckbox } from './components/HasOpenPriceCheckbox';
import { InStopCheckbox } from './components/InStopCheckbox';
import { IsCommentsCheckbox } from './components/IsCommentsCheckbox';
import { IsFiscalizedCheckbox } from './components/IsFiscalizedCheckbox';
import { PortionSelectionModeRadio } from './components/PortionSelectionModeRadio';
import { TaxPercentField } from './components/TaxPercentField';
import { IsProtectedFromSyncCheckbox } from './components/IsProtectedFromSyncCheckbox';
import { ActivationScheduleInput } from './components/ActivationScheduleInput';
import { DeactivationScheduleInput } from './components/DeactivationScheduleInput';
import CronGenerator from 'components/CronGenerator/CronGenerator';
import { ModifierGroupsMultipleSelect } from './components/ModifierGroupsMultipleSelect';
import { BtnBlock } from './components/BtnBlock';
import style from './InputFields.module.scss';

export const InputFields: React.FC = () => {
	const {
		setIDRestaurant,
		changePrice,
		changeOpenPrice,
		changeOpenPriceMaxThreshold,
		toggleHasOpenPrice,
		toggleInStop,
		toggleIsCommentVisible,
		toggleIsFiscalized,
		changePortionSelectionMode,
		changeTaxPercent,
		toggleIsTaxPercentProtectedFromSync,
		changeActivationSchedule,
		changeDeactivationSchedule,
		setModifierGroups,
		setModifierGroupsWithAdditionalData,
		setDataForSubmit,
		changeTypeOfModalWindow,
		dishId,
		customerName,
		dishName,
		isCreatedOnPos,
		fetchedRestaurants,
		restaurantsWithPortionsIds,
		IDSelectedRestaurant,
		categories,
		price,
		openPrice,
		openPriceMaxThreshold,
		hasOpenPrice,
		inStop,
		isCommentVisible,
		isFiscalized,
		portionSelectionMode,
		taxPercent,
		isTaxPercentProtectedFromSync,
		activationSchedule,
		deactivationSchedule,
		IDsSelectedModifierGroups,
		modifierGroupsWithAdditionalData,
		hasPortionBasedModGroups,
	} = useContext<IEditDishRestaurantPropertiesContext>(EditDishRestaurantPropertiesContext);

	const hasPortionsForSelectedRestaurant =
		IDSelectedRestaurant && restaurantsWithPortionsIds.includes(IDSelectedRestaurant);
	const [form] = Form.useForm();

	useEffect(() => {
		form.setFieldsValue({
			IDSelectedRestaurant,
			price,
			openPrice,
			openPriceMaxThreshold,
			hasOpenPrice,
			inStop,
			isCommentVisible,
			isFiscalized,
			portionSelectionMode,
			taxPercent,
			isTaxPercentProtectedFromSync,
			activationSchedule,
			deactivationSchedule,
			IDsSelectedModifierGroups,
		});
		modifierGroupsWithAdditionalData.forEach((el) => {
			form.setFieldsValue({
				[`downLimit-${el.id}`]: el.downLimit,
				[`upLimit-${el.id}`]: el.upLimit,
			});
		});
	}, [
		IDSelectedRestaurant,
		price,
		openPrice,
		openPriceMaxThreshold,
		hasOpenPrice,
		inStop,
		isCommentVisible,
		isFiscalized,
		portionSelectionMode,
		taxPercent,
		isTaxPercentProtectedFromSync,
		activationSchedule,
		deactivationSchedule,
		IDsSelectedModifierGroups,
		modifierGroupsWithAdditionalData,
	]);

	const submit = async () => {
		const assignments = categories.map((category) => ({
			categoryId: category.id,
			subCategoryIds: category.subCategories ? category.subCategories.map((subCategory) => subCategory.id) : [],
		}));
		const modifierGroups = modifierGroupsWithAdditionalData.map((modifierGroupWithAdditionalData) => ({
			id: modifierGroupWithAdditionalData.id,
			upLimit: modifierGroupWithAdditionalData.upLimit,
			downLimit: modifierGroupWithAdditionalData.downLimit,
		}));

		setDataForSubmit({
			dishId,
			restaurantId: IDSelectedRestaurant || '',
			price,
			openPrice,
			openPriceMaxThreshold,
			hasOpenPrice,
			isDisabled: inStop,
			isCommentVisible,
			isFiscalized,
			portionSelectionMode,
			taxPercent,
			isTaxPercentProtectedFromSync,
			activationCron: activationSchedule,
			deactivationCron: deactivationSchedule,
			assignments,
			modifierGroups,
		});
		changeTypeOfModalWindow('toConfirmSubmit');
	};

	const handleClickAssignSubcategories = useCallback(
		() => changeTypeOfModalWindow('assignSubcategories'),
		[changeTypeOfModalWindow],
	);

	const revalidateForm = form.validateFields;

	return (
		<div data-test="InputFields">
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				<Text
					className={style.name}
					translationKey={dishName}
					size="xxl"
					weight="bold"
					display="block"
					withTooltip={true}
				/>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="restaurantPropertiesHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<div className={style.mainProps}>
						<FullInfoField label="customerLabel" value={customerName} useInForm={true} />
						<div />
					</div>
					<RestaurantSelect
						setIDRestaurant={setIDRestaurant}
						fetchedRestaurants={fetchedRestaurants}
						selectedRestaurantId={IDSelectedRestaurant}
					/>
					{IDSelectedRestaurant && !!categories.length && (
						<>
							<Text
								className={style.dishLabel}
								translationNameSpace="form"
								translationKey="categoryAndSubcategoryHeading"
								theme="neutralLight"
								size="xs"
							/>
							<CategoryList items={categories} onClickShowAssignModal={handleClickAssignSubcategories} />
						</>
					)}
					{IDSelectedRestaurant && !categories.length && (
						<NoItemsNotification
							title="noCategoriesAssigned"
							description="assignCategoriesToCustomersToBeAbleToEditThem"
							assignBtnTranslationKey="assignCategoriesButton"
							onClickShowAssignModal={handleClickAssignSubcategories}
						/>
					)}
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="priceHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<div className={style.row}>
						<div>
							<PortionSelectionModeRadio
								changePortionSelectionMode={changePortionSelectionMode}
								isDisabled={!IDSelectedRestaurant || isCreatedOnPos}
							/>
							{hasPortionsForSelectedRestaurant ? (
								<Text
									translationNameSpace="form"
									translationKey="warningPriceHiddenByPortions"
									theme="secondary"
									size="s"
									display="inline"
								/>
							) : (
								<PriceInput changePrice={changePrice} isDisabled={isCreatedOnPos} />
							)}
							<InStopCheckbox
								toggleInStop={toggleInStop}
								inStop={inStop}
								isDisabled={!IDSelectedRestaurant}
							/>
							<IsCommentsCheckbox
								toggleIsCommentVisible={toggleIsCommentVisible}
								isCommentVisible={isCommentVisible}
								isDisabled={!IDSelectedRestaurant}
							/>
							<IsFiscalizedCheckbox
								toggleIsFiscalized={toggleIsFiscalized}
								isFiscalized={isFiscalized}
								isDisabled={!IDSelectedRestaurant}
							/>
						</div>
						<div>
							<OpenPriceInput changeOpenPrice={changeOpenPrice} isDisabled={!hasOpenPrice} />
							{hasOpenPrice && (
								<OpenPriceMaxThresholdInput changeOpenPriceMaxThreshold={changeOpenPriceMaxThreshold} />
							)}
							<HasOpenPriceCheckbox
								toggleHasOpenPrice={toggleHasOpenPrice}
								hasOpenPrice={hasOpenPrice}
								isDisabled={!IDSelectedRestaurant}
							/>
						</div>
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="taxRateHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<TaxPercentField changeTaxPercent={changeTaxPercent} />
					<IsProtectedFromSyncCheckbox
						toggleIsTaxPercentProtectedFromSync={toggleIsTaxPercentProtectedFromSync}
						isTaxPercentProtectedFromSync={isTaxPercentProtectedFromSync}
						isDisabled={!IDSelectedRestaurant}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="scheduleHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<div className={style.row}>
						<ActivationScheduleInput changeActivationSchedule={changeActivationSchedule} />
						<DeactivationScheduleInput changeDeactivationSchedule={changeDeactivationSchedule} />
					</div>
					<CronGenerator />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="modifierGroupsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					{IDSelectedRestaurant ? (
						<>
							{!hasPortionBasedModGroups && (
								<ModifierGroupsMultipleSelect
									setModifierGroups={setModifierGroups}
									setModifierGroupsWithAdditionalData={setModifierGroupsWithAdditionalData}
									productId={dishId}
									restaurantId={IDSelectedRestaurant}
									IDsSelectedModifierGroups={IDsSelectedModifierGroups}
									modifierGroupsWithAdditionalData={modifierGroupsWithAdditionalData}
								/>
							)}
							{hasPortionBasedModGroups && (
								<Text
									translationNameSpace="form"
									translationKey="warningModGroupsHiddenByPortions"
									theme="secondary"
									size="s"
									display="inline"
								/>
							)}
						</>
					) : (
						<Text translationNameSpace="form" translationKey="-" />
					)}
				</div>
				<BtnBlock revalidateForm={revalidateForm} />
			</Form>
		</div>
	);
};
